import { Injectable } from '@angular/core';
import { CustomLayoutSpecialField } from './Models/custom-layout-special-field.model';
import { DataQueryField } from '../../classroom360/data-query/data-query-field.model';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { System } from '../../shared-components/System';
import { CustomLayout } from './Models/custom-layout.model';
import { YearService } from '../../shared-components/year/year.service';
import { CustomLayoutType } from './Models/custom-layout-type.model';
import { DataQueryResult } from '../../classroom360/data-query/data-query-result.model';
import { CustomLayoutSection } from './Models/custom-layout-section.model';
import { first } from 'rxjs/operators';
import { SQLParameter } from './Models/sql-parameter.model';
import { Lookup } from '../../shared-components/lookup/lookup.model';
import * as _ from 'lodash';
import { UserPermission } from '../../shared-components/user/user-permission.model';
import { CustomLayoutSchoolDefault } from './Models/custom-layout-school-default.model';
import { LoginService } from '../../shared-modules/login/login.service';
import { Router } from '@angular/router';

@Injectable()
export class CustomLayoutsService {

    specialFields: CustomLayoutSpecialField[] = [];
    layoutTypes: CustomLayoutType[] = [];
    selectedLayoutType: CustomLayoutType;
    dynamicComponentMap = {};
    sectionData: CustomLayoutSectionDataContainer[] = [];
    numericDataTypes = ['decimal', 'int', 'bigint', 'float', 'money'];
    textDataTypes = ['char', 'varchar', 'nvarchar', 'text'];
    dateTimeDataTypes = ['time', 'datetime', 'date'];
    useCurrentYearOnly = false;
    v10YearFieldNames = ['Academic_Year', 'AYIdentifier'];
    incidentCodes: Lookup[] = [];
    public incidentSubCodeId: number = 0;
    private _externalFilters: DataQueryField[];
    public observableexternalFilters = new Subject<DataQueryField[]>();
    observableexternalFiltersStream$ = this.observableexternalFilters.asObservable();

    public get externalFilters(): DataQueryField[] {
        return this._externalFilters;
    }

    public set externalFilters(value: DataQueryField[]) {
        this._externalFilters = value;
        this.observableexternalFilters.next(value);
    }

    private _savedLayouts: CustomLayout[];
    public get savedLayouts(): CustomLayout[] {
        return this._savedLayouts;
    }

    public set savedLayouts(value: CustomLayout[]) {
        this._savedLayouts = value;
        this.observableSavedLayouts.next(this._savedLayouts);
    }

    private _selectedLayoutViewType: CustomLayoutType;
    public get selectedLayoutViewType(): CustomLayoutType {
        return this._selectedLayoutViewType;
    }

    public set selectedLayoutViewType(value: CustomLayoutType) {
        this._selectedLayoutViewType = value;
        this.observableSelectedLayoutViewType.next(this._selectedLayoutViewType);
    }

    public observableSelectedLayoutViewType = new Subject<CustomLayoutType>();
    observableSelectedLayoutViewTypeStream$ = this.observableSelectedLayoutViewType.asObservable();

    public observableSavedLayouts = new Subject<CustomLayout[]>();
    observableSavedLayoutsStream$ = this.observableSavedLayouts.asObservable();

    private _selectedLayout: CustomLayout;
    public get selectedLayout(): CustomLayout {
        if (this._selectedLayout && this._selectedLayout.LayoutTypeID == this.selectedLayoutType.Id) {
            return this._selectedLayout;
        } else {
            return null;
        }
    }

    public set selectedLayout(value: CustomLayout) {
        if (value) {
            if (!this.loginService.LoginSettings.V10 || this._selectedLayout) {
                this.updateUserLayoutHistory(value.Id, this.router.url.indexOf('academicHistory') > -1).pipe(first()).subscribe(() => {
                });
            }
            this._selectedLayout = _.cloneDeep(value);
        } else {
            this._selectedLayout = null;
        }
        this.observableSelectedLayout.next(this._selectedLayout);
    }

    public observableSelectedLayout = new Subject<CustomLayout>();
    observableSelectedLayoutStream$ = this.observableSelectedLayout.asObservable();

    private _dataQueryFields: DataQueryField[] = [];
    public get dataQueryFields(): DataQueryField[] {
        return this._dataQueryFields;
    }

    public set dataQueryFields(value: DataQueryField[]) {
        this._dataQueryFields = value;
        this.observableDataQueryFields.next(this._dataQueryFields);
    }

    public observableDataQueryFields = new Subject<DataQueryField[]>();
    observableDataQueryFieldsStream$ = this.observableDataQueryFields.asObservable();

    private _userPermissions: UserPermission[];
    public get userPermissions(): UserPermission[] {
        return this._userPermissions;
    }

    public set userPermissions(value: UserPermission[]) {
        this._userPermissions = value;
        this.observableUserPermissions.next(this._userPermissions);
    }

    public observableUserPermissions = new Subject<UserPermission[]>();
    observableUserPermissionsStream$ = this.observableUserPermissions.asObservable();

    private _validActionCodes: string;
    public get validActionCodes(): string {
        return this._validActionCodes;
    }

    public set validActionCodes(value: string) {
        this._validActionCodes = value;
        this.observableincidentCode.next(this._validActionCodes);
    }

    public observableincidentCode = new Subject<string>();
    observableincidentCodeStream$ = this.observableincidentCode.asObservable();

    private _FieldDataLoaded: boolean = true;

    public get fieldDataLoaded(): boolean {
        return this._FieldDataLoaded;
    }

    public set fieldDataLoaded(value: boolean) {
        this._FieldDataLoaded = value;
        this.observableFieldDataLoaded.next(value);
    }

    public observableFieldDataLoaded = new Subject<boolean>();
    observableFieldDataLoadedStream$ = this.observableFieldDataLoaded.asObservable();

    constructor(
        private http: HttpClient,
        private yearService: YearService,
        private loginService: LoginService,
        private router: Router
    ) {
    }

    getLayoutById(layoutId: number): Observable<CustomLayout> {
        const queryString = '?layoutId=' + layoutId;
        return this.http.get<CustomLayout>(System.apiURL.Student + 'GetLayoutById' + queryString);
    }

    getLayoutsByType(typeId: number, yearId: number, academicHistory: boolean): Observable<CustomLayout[]> {
        const queryString = '?typeId=' + typeId +
            '&yearId=' + yearId +
            '&academicHistory=' + academicHistory;

        return this.http.get<CustomLayout[]>(System.apiURL.Student + 'GetLayoutsByType' + queryString);
    }

    getSpecialFields(): Observable<CustomLayoutSpecialField[]> {
        return this.http.get<CustomLayoutSpecialField[]>(System.apiURL.Student + 'GetCustomLayoutSpecialFields');
    }

    getLayoutTypes(): Observable<CustomLayoutType[]> {
        return this.http.get<CustomLayoutType[]>(System.apiURL.Student + 'GetCustomLayoutTypes');
    }

    getDataQueryFields(objectName: string): Observable<DataQueryField[]> {
        const dqobjectName = (objectName == null || objectName == '') ? '' : objectName;
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id +
            '&objectName=' + dqobjectName;
        return this.http.get<DataQueryField[]>(System.apiURL.Classroom + 'GetDataQueryFields' + queryString);
    }

    saveLayout(layout: CustomLayout): Observable<CustomLayout> {
        return this.http.post<CustomLayout>(System.apiURL.Student + 'SaveCustomLayout', layout);
    }

    deleteLayout(layoutId: number): Observable<string> {
        if (this._selectedLayout.Id == layoutId) {
            const layout = this.savedLayouts.find(o => o.Id == layoutId);
            if (layout) {
                const defaultLayout = this.savedLayouts.find(o => o.LayoutTypeID == layout.LayoutTypeID && o.TylerDefault == true);
                if (defaultLayout) {
                    this.selectedLayout = defaultLayout;
                } else {
                    this.selectedLayout = null;
                }
            } else {
                this.selectedLayout = null;
            }
        }
        const queryString = '?layoutId=' + layoutId;
        return this.http.get<string>(System.apiURL.Student + 'DeleteCustomLayout' + queryString);
    }

    setUserLayoutTypeToDefault(layoutTypeId: number): Observable<boolean> {
        const queryString = '?layoutTypeId=' + layoutTypeId;
        return this.http.get<boolean>(System.apiURL.Student + 'SetUserLayoutTypeToDefault' + queryString);
    }

    updateUserLayoutHistory(layoutId: number, academicHistory: boolean): Observable<void> {
        const queryString = '?layoutId=' + layoutId + '&academicHistory=' + academicHistory;
        return this.http.get<void>(System.apiURL.Student + 'UpdateUserLayoutHistory' + queryString);
    }

    setUserLayoutToDefault(layoutTypeId: number): Observable<void> {
        const queryString = '?layoutTypeId=' + layoutTypeId;
        return this.http.get<void>(System.apiURL.Student + 'SetUserLayoutToDefault' + queryString);
    }

    getUserDefaultLayout(layoutTypeId: number, schoolId: number, academicHistory: boolean): Observable<number> {
        let queryString = '?layoutTypeId=' + layoutTypeId;
        queryString += '&schoolId=' + schoolId;
        queryString += '&yearId=' + this.yearService.defaultYear.Id;
        queryString += '&academicHistory=' + academicHistory;
        return this.http.get<number>(System.apiURL.Student + 'GetUserDefaultLayout' + queryString);
    }

    getSectionData(sectionId: number, fields: DataQueryField[], schoolId: number, yearId: number, currentYearOnly: boolean, filterAllowedStudents: boolean, filterAllowedSchools: boolean, studentIds?: string): Observable<DataQueryResult> {
        const studentId = (studentIds != null || studentIds != '') ? studentIds : '';
        let queryString = '?sectionId=' + sectionId;
        queryString += '&schoolId=' + schoolId;
        queryString += '&yearId=' + yearId;
        queryString += '&currentYearOnly=' + currentYearOnly;
        queryString += '&filterAllowedStudents=' + filterAllowedStudents;
        queryString += '&filterAllowedSchools=' + filterAllowedSchools;
        queryString += '&studentIds=' + studentId;
        return this.http.post<DataQueryResult>(System.apiURL.Student + 'GetSectionData' + queryString, fields);
    }

    getCustomLayoutSchoolDefaults(layoutType_Id: number): Observable<CustomLayoutSchoolDefault[]> {
        const queryString = '?layoutType_Id=' + layoutType_Id +
            '&yearId=' + this.yearService.defaultYear.Id;
        return this.http.get<CustomLayoutSchoolDefault[]>(System.apiURL.Student + 'GetCustomLayoutSchoolDefaults' + queryString);
    }

    saveDefaultSchoolLayouts(defaultSchoolLayouts: CustomLayoutSchoolDefault[]): Observable<any> {
        return this.http.post<any>(System.apiURL.Student + 'SaveDefaultSchoolLayouts', defaultSchoolLayouts);
    }

    getSectionDataFromSP(spName: string, parameters: SQLParameter[]): Observable<DataQueryResult> {
        const queryString = '?spName=' + spName;
        return this.http.post<DataQueryResult>(System.apiURL.Student + 'GetSectionDataFromSP' + queryString, parameters);
    }

    getV10DataQueryFieldLookup(dqFieldId: number, filterString: string): Observable<Lookup[]> {
        let queryString = '?dqFieldId=' + dqFieldId;
        queryString += '&filterString=' + filterString;
        return this.http.get<Lookup[]>(System.apiURL.Student + 'GetV10DataQueryFieldLookup' + queryString);
    }

    getV9DataQueryFieldLookup(dqFieldId: number, filterString: string): Observable<Lookup[]> {
        let queryString = '?dqFieldId=' + dqFieldId;
        queryString += '&filterString=' + filterString;
        queryString += '&yearId=' + this.yearService.defaultYear.Id;
        queryString += '&schoolId=' + this.yearService.defaultYear.SchoolID;
        return this.http.get<Lookup[]>(System.apiURL.Student + 'GetV9DataQueryFieldLookup' + queryString);
    }

    performDataQueryUpdate(fields: DataQueryField[]): Observable<DataQueryResult[]> {
        return this.http.post<DataQueryResult[]>(System.apiURL.Student + 'PerformDataQueryUpdate', fields);
    }

    getFilterFields(sectionId: number): DataQueryField[] {
        const filterFields: DataQueryField[] = [];
        const section = this.selectedLayout.Sections.find(o => o.Id == sectionId);
        if (section) {
            const clFields = section.AvailableFields.filter(o => o.RequiredForFilter == true);
            clFields.forEach(field => {
                if (field.DQFieldID > 0) {
                    const dqField = this.dataQueryFields.find(o => o.ID == field.DQFieldID);
                    if (dqField) {
                        filterFields.push(_.cloneDeep(dqField));
                    }
                }
            });
            if (this.useCurrentYearOnly) {
                const dqAYFieldIds = this.dataQueryFields.filter(o => this.v10YearFieldNames.includes(o.FieldName)).map(o => o.ID);
                if (filterFields.find(o => dqAYFieldIds.includes(o.ID)) != null) {
                    // already filtering on academic year
                } else {
                    // need to add academic year filter field
                    const yearField = section.AvailableFields.find(o => dqAYFieldIds.includes(o.DQFieldID));
                    if (yearField) {
                        const dqField = this.dataQueryFields.find(o => o.ID == yearField.DQFieldID);
                        if (dqField) {
                            filterFields.push(_.cloneDeep(dqField));
                        }
                    }
                }
            }
        }

        return filterFields;
    }

    getFieldList(section: CustomLayoutSection, includeAllChildFields: boolean): DataQueryField[] {
        const fields: DataQueryField[] = [];
        const childSections = this.selectedLayout.Sections.filter(s => s.ParentSectionID == section.Id);
        section.SelectedFields.forEach(sf => {
            if (sf.DQFieldID > 0) {
                let dqField: any;
                if (this.loginService.LoginSettings.V10) {
                    dqField = this.dataQueryFields.find(d => d.ID == sf.DQFieldID);
                } else {
                    dqField = this.dataQueryFields.find(d => d.ID == sf.DQFieldID && d.CategoryID == sf.CategoryId);
                }
                if (dqField) {
                    dqField = _.cloneDeep(dqField);
                    dqField.ShowOnReport = true;
                    if (sf.SortByDefault) {
                        dqField.SortDirection = sf.SortDirection;
                        dqField.SortOrder = sf.SortOrder;
                        dqField.SortByDefault = true;
                    }
                    fields.push(dqField);
                }
            } else {
                const spField = this.specialFields.find(o => o.Id == sf.SpecialFieldID);
                if (spField && spField.IsSection) {
                    const childSection = this.selectedLayout.Sections.find(o => o.Id == spField.SectionId);
                    if (childSection && childSections.find(o => o.Id == childSection.Id) == null) {
                        childSections.push(childSection);
                    }
                }
            }
        });
        section.AvailableFields.forEach(sf => {
            if (sf.DQFieldID > 0 && (sf.RequiredForDisplay || sf.RequiredForFilter || sf.AlwaysIncludeInResults) && fields.find(o => o.ID == sf.DQFieldID) == null) {
                let dqField: any;
                if (this.loginService.LoginSettings.V10) {
                    dqField = this.dataQueryFields.find(d => d.ID == sf.DQFieldID);
                } else {
                    dqField = this.dataQueryFields.find(d => d.ID == sf.DQFieldID && d.CategoryID == sf.CategoryId);
                }
                if (dqField) {
                    dqField = _.cloneDeep(dqField);
                    dqField.ShowOnReport = sf.RequiredForDisplay;
                    dqField.IncludeInResults = sf.RequiredForFilter || sf.AlwaysIncludeInResults;
                    if (sf.SortByDefault) {
                        dqField.SortDirection = sf.SortDirection;
                        dqField.SortOrder = sf.SortOrder;
                    }
                    fields.push(dqField);
                }
            }
        });

        let fieldsRequiredByChildren: DataQueryField[] = [];
        fieldsRequiredByChildren = this.getAllFieldsForChildSections(childSections, includeAllChildFields);
        fieldsRequiredByChildren.forEach(field => {
            if (fields.find(o => o.ID == field.ID) == null) {
                fields.push(field);
            }
        });
        return fields;
    }

    getFieldsForChildSectionFilters(childSection: CustomLayoutSection): DataQueryField[] {
        const fieldsRequiredByChildren: DataQueryField[] = [];
        childSection.AvailableFields.forEach(field => {
            if (field.RequiredForFilter && field.DQFieldID > 0 && fieldsRequiredByChildren.find(o => o.ID == field.DQFieldID) == null) {
                let dqField: any;
                if (this.loginService.LoginSettings.V10) {
                    dqField = this.dataQueryFields.find(d => d.ID == field.DQFieldID);
                } else {
                    dqField = this.dataQueryFields.find(d => d.ID == field.DQFieldID && d.CategoryID == field.CategoryId);
                }

                if (dqField) {
                    dqField = _.cloneDeep(dqField);
                    dqField.ShowOnReport = false;
                    dqField.IncludeInResults = true;
                    fieldsRequiredByChildren.push(dqField);
                }
            }
        });
        return fieldsRequiredByChildren;
    }

    getAllFieldsForChildSections(childSections: CustomLayoutSection[], includeAllChildFields: boolean): DataQueryField[] {
        let fieldsRequiredByChildren: DataQueryField[] = [];
        childSections.forEach(section => {
            if (section.IsGrid || !includeAllChildFields) {
                this.getFieldsForChildSectionFilters(section);
            } else {
                fieldsRequiredByChildren = fieldsRequiredByChildren.concat(this.getFieldList(section, true));
            }
        });
        return fieldsRequiredByChildren;
    }

    getParameters(section: CustomLayoutSection, rowIndex: number = 0): SQLParameter[] {
        // need to specify parameters specific to the sp being called
        switch (section.SPName) {
            case 'spCustomLayoutAcademicHistorySubGrid': {
                return this.getAHSubGridParameters(section, rowIndex);
            }
            case 'sp_CustomLayoutAcademicHistorySubGrid': {
                return this.getV9AHSubGridParameters(section, rowIndex);
            }
            default: {
                return [];
            }
        }
    }

    getAHSubGridParameters(section: CustomLayoutSection, rowIndex: number): SQLParameter[] {
        const parentData = this.sectionData.find(o => o.sectionId == section.ParentSectionID);
        const parameters: SQLParameter[] = [];
        if (parentData) {
            let dataBucketScoreId = '-1';
            const colIndex = parentData.data.Columns.findIndex(o => o.FieldName == 'DataBucketScore_ID');
            if (colIndex >= 0) {
                dataBucketScoreId = parentData.data.Data[rowIndex][colIndex];
            }
            parameters.push({ ParameterName: '@DataBucketScore_ID', Value: dataBucketScoreId });
            const selectedSlots: string[] = [];
            section.SelectedFields.forEach(field => {
                if (field.SpecialFieldID > 0) {
                    const spField = this.specialFields.find(o => o.Id == field.SpecialFieldID);
                    if (spField && spField.DefinesRows) {
                        switch (spField.FieldName) {
                            case 'examProgressGrades': {
                                selectedSlots.push('CW1,CW2,CW3,CW4,CW5,CW6,CW7,CW8,CW9,CW10');
                                break;
                            }
                            case 'termGrades': {
                                selectedSlots.push('T1,T2,T3,T4,T5,T6,T7,T8,T9,T10');
                                break;
                            }
                            case 'gradingPeriodGrades': {
                                selectedSlots.push('GP1,GP2,GP3,GP4,GP5,GP6,GP7,GP8,GP9,GP10');
                                break;
                            }
                            case 'semesterGrades': {
                                selectedSlots.push('S1,S2,S3,S4');
                                break;
                            }
                            case 'yearEndGrades': {
                                selectedSlots.push('CW1,CW2,CW3,CW4,CW5,CW6,CW7,CW8,CW9,CW10');
                                break;
                            }
                            case 'finalScore': {
                                selectedSlots.push('final');
                                break;
                            }
                        }
                    }
                }
            });
            parameters.push({ ParameterName: '@SelectedSlots', Value: selectedSlots.join() });
        }
        return parameters;
    }

    getV9AHSubGridParameters(section: CustomLayoutSection, rowIndex: number): SQLParameter[] {
        const parentData = this.sectionData.find(o => o.sectionId == section.ParentSectionID);
        const parameters: SQLParameter[] = [];
        if (parentData) {
            let lngStudentId = '-1';
            let lngSiteSubjectListId = '-1';
            let lngYearId = '-1';
            let lngDistrictSubjectId = '-1';
            let colIndex = -1;
            colIndex = parentData.data.Columns.findIndex(o => o.FieldName == 'lngStudentId');
            if (colIndex >= 0) {
                lngStudentId = parentData.data.Data[rowIndex][colIndex];
            }
            parameters.push({ ParameterName: '@lngStudentId', Value: lngStudentId });

            colIndex = parentData.data.Columns.findIndex(o => o.FieldName == 'lngSiteSubjectListId');
            if (colIndex >= 0) {
                lngSiteSubjectListId = parentData.data.Data[rowIndex][colIndex];
            }
            parameters.push({ ParameterName: '@lngSiteSubjectListId', Value: lngSiteSubjectListId });

            colIndex = parentData.data.Columns.findIndex(o => o.FieldName == 'lngDistrictSubjectId');
            if (colIndex >= 0) {
                lngDistrictSubjectId = parentData.data.Data[rowIndex][colIndex];
            }
            parameters.push({ ParameterName: '@lngDistrictSubjectId', Value: lngDistrictSubjectId });

            colIndex = parentData.data.Columns.findIndex(o => o.FieldName == 'lngYearId');
            if (colIndex >= 0) {
                lngYearId = parentData.data.Data[rowIndex][colIndex];
            }
            parameters.push({ ParameterName: '@lngYearId', Value: lngYearId });

            const selectedSlots: string[] = [];
            section.SelectedFields.forEach(field => {
                selectedSlots.push(field.DQFieldID.toString());
            });
            parameters.push({ ParameterName: '@strSelectedFields', Value: selectedSlots.join() });
        }
        return parameters;
    }

    getSpecialColumns(section: CustomLayoutSection): string[] {
        const columns: string[] = [];
        section.SelectedFields.forEach(field => {
            if (field.SpecialFieldID > 0) {
                const spField = this.specialFields.find(o => o.Id == field.SpecialFieldID);
                if (spField && spField.DefinesColumns) {
                    switch (spField.DisplayName) {
                        // SP column names should match special field display name, but if they don't then you can make exceptions here
                        // Example:
                        // case 'Number Grade': {
                        //   columns.push('Numeric Grade');
                        //   break;
                        // }
                        default: {
                            columns.push(spField.DisplayName);
                        }
                    }
                }
            }
        });
        return columns;
    }

    storeSectionData(sectionId: number, data: DataQueryResult) {
        let sd: CustomLayoutSectionDataContainer = this.sectionData.find(o => o.sectionId == sectionId);
        if (sd) {
            sd.data = data;
        } else {
            sd = { sectionId: sectionId, data: data, filters: [] };
            this.sectionData.push(sd);
        }
    }

    getStoredSectionData(sectionId: number, rowIndex: number = -1): DataQueryResult {
        const sd: CustomLayoutSectionDataContainer = this.sectionData.find(o => o.sectionId == sectionId);
        if (sd) {
            return sd.data;
        } else {
            return new DataQueryResult();
        }
    }

    getFilterValueFromParent(sectionId: number, rowIndex: number, fieldId: number): any {
        let val: any = null;
        const section = this.selectedLayout.Sections.find(o => o.Id == sectionId);
        if (section && section.ParentSectionID > 0) {
            const parentData = this.sectionData.find(o => o.sectionId == section.ParentSectionID);
            if (parentData) {
                const colIndex = parentData.data.Columns.findIndex(o => o.ID == fieldId);
                if (colIndex >= 0 && parentData.data && parentData.data.Data && parentData.data.Data[rowIndex]) {
                    val = parentData.data.Data[rowIndex][colIndex];
                }
            }
        }
        return val;
    }

    getDefaultValueForFilter(field: DataQueryField): any {
        if (this.numericDataTypes.find(o => o.toLowerCase() == field.DataType.toLowerCase())) {
            return -1;
        } else if (this.textDataTypes.find(o => o.toLowerCase() == field.DataType.toLowerCase())) {
            return '';
        } else if (this.dateTimeDataTypes.find(o => o.toLowerCase() == field.DataType.toLowerCase())) {
            return new Date(-8640000000000000);
        } else if (field.DataType.toLowerCase() == 'bit') {
            return false;
        } else {
            return 0;
        }
    }
}

export interface ComponentMap {
    name: string;
    component: any;
}

interface CustomLayoutSectionDataContainer {
    sectionId: number;
    data: DataQueryResult;
    filters: DataQueryField[];
}


